import type { EncryptedBlob } from "@agencylens/shared";

/**
 * AES-GCM encryption helpers, plus per-buyer envelope-encryption (DEK)
 * helpers. Runs identically in the browser (Web Crypto) and in Cloudflare
 * Workers.
 *
 * KEY MODEL (post-G3 hardening):
 *
 *   Every buyer gets a random 256-bit Data Encryption Key (DEK), minted by
 *   the license worker at signup. The DEK is the `password` passed to
 *   encryptToken/decryptToken for that buyer's blobs — so buyer A's tokens
 *   are encrypted with a key buyer B never sees.
 *
 *   At rest the DEK is *wrapped* (encrypted) with a Worker-only secret
 *   (DEK_WRAPPING_SECRET) via wrapDek/unwrapDek and stored in KV. The
 *   plaintext DEK is delivered to the buyer's browser only over an
 *   authenticated HTTPS response (/license/verify, /license/me) and held in
 *   sessionStorage. The cron + other workers unwrap the DEK from KV using
 *   the wrapping secret at fire time.
 *
 *   Net isolation: decrypting any blob requires the per-buyer DEK, which
 *   requires EITHER the Worker-only wrapping secret (never shipped to the
 *   browser) OR an authenticated session for that specific buyer. A KV-only
 *   leak or a shared-machine IndexedDB read no longer yields plaintext.
 *
 * There is intentionally NO global fallback password. A missing key is an
 * error, not a silent default.
 *
 * KEY DERIVATION: HKDF-SHA256 (not PBKDF2). Every key passed in here is now
 * full-entropy keying material — a 256-bit DEK, or the operator's random
 * DEK_WRAPPING_SECRET. PBKDF2's iteration stretch only buys security against
 * low-entropy passphrases, of which we have none; HKDF is the correct KDF for
 * high-entropy input and is ~four orders of magnitude cheaper, which matters
 * in the cron worker (it derives a key per blob, per buyer, every run).
 */

const SALT_BYTES = 16;
const IV_BYTES = 12;
const KEY_LENGTH_BITS = 256;
/** Size of a freshly generated per-buyer DEK, in bytes (256-bit). */
const DEK_BYTES = 32;
/** Current EncryptedBlob schema version (HKDF-SHA256 derivation). */
const BLOB_VERSION = 2;
/** Domain-separation label mixed into HKDF for AES-GCM keys. */
const HKDF_INFO = new TextEncoder().encode("agencylens/aes-gcm/v2");

/* ------------------------------------------------------------------ utils */

function getSubtle(): SubtleCrypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c || !c.subtle) {
    throw new Error(
      "Web Crypto API (crypto.subtle) is not available. This package requires a modern browser or Node 20+.",
    );
  }
  return c.subtle;
}

function getRandomBytes(length: number): Uint8Array {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c) throw new Error("crypto.getRandomValues not available");
  const buf = new Uint8Array(length);
  c.getRandomValues(buf);
  return buf;
}

function bytesToBase64(bytes: Uint8Array): string {
  // Works in both browsers and Node 20+ (which has btoa as a global).
  let bin = "";
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]!);
  }
  // eslint-disable-next-line no-undef
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  // eslint-disable-next-line no-undef
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

/* ----------------------------------------------------------------- derive */

async function deriveKey(
  password: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const subtle = getSubtle();
  const ikm = new TextEncoder().encode(password);
  // SubtleCrypto requires a fresh ArrayBuffer in some runtimes; copy to be safe.
  const ikmBuf = new Uint8Array(ikm);
  const saltBuf = new Uint8Array(salt);
  const baseKey = await subtle.importKey(
    "raw",
    ikmBuf,
    { name: "HKDF" },
    false,
    ["deriveKey"],
  );
  return subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: saltBuf,
      info: new Uint8Array(HKDF_INFO),
    },
    baseKey,
    { name: "AES-GCM", length: KEY_LENGTH_BITS },
    false,
    ["encrypt", "decrypt"],
  );
}

/* ------------------------------------------------------------ encrypt API */

export async function encryptToken(
  plaintext: string,
  password: string,
): Promise<EncryptedBlob> {
  const subtle = getSubtle();
  const salt = getRandomBytes(SALT_BYTES);
  const iv = getRandomBytes(IV_BYTES);
  const key = await deriveKey(password, salt);

  const plaintextBytes = new TextEncoder().encode(plaintext);
  const ciphertext = await subtle.encrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv) },
    key,
    new Uint8Array(plaintextBytes),
  );

  return {
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
    iv: bytesToBase64(iv),
    salt: bytesToBase64(salt),
    version: BLOB_VERSION,
  };
}

export async function decryptToken(
  blob: EncryptedBlob,
  password: string,
): Promise<string> {
  if (blob.version !== BLOB_VERSION) {
    throw new Error(`Unsupported EncryptedBlob version: ${blob.version}`);
  }
  const subtle = getSubtle();
  const salt = base64ToBytes(blob.salt);
  const iv = base64ToBytes(blob.iv);
  const ciphertext = base64ToBytes(blob.ciphertext);
  const key = await deriveKey(password, salt);

  const plaintextBytes = await subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv) },
    key,
    new Uint8Array(ciphertext),
  );
  return new TextDecoder().decode(plaintextBytes);
}

/* ----------------------------------------------- convenience for object payloads */

export async function encryptJson<T>(
  payload: T,
  password: string,
): Promise<EncryptedBlob> {
  return encryptToken(JSON.stringify(payload), password);
}

export async function decryptJson<T>(
  blob: EncryptedBlob,
  password: string,
): Promise<T> {
  const text = await decryptToken(blob, password);
  return JSON.parse(text) as T;
}

/* ----------------------------------------------- per-buyer DEK envelope */

/**
 * Mint a fresh per-buyer Data Encryption Key — 256 random bits, base64.
 * Generated once per buyer at signup; used as the `password` for all of
 * that buyer's encryptToken/decryptToken calls.
 */
export function generateDek(): string {
  return bytesToBase64(getRandomBytes(DEK_BYTES));
}

/**
 * Wrap (encrypt) a DEK with a Worker-only wrapping secret for at-rest
 * storage in KV. Returns an EncryptedBlob, same envelope format as any
 * other ciphertext.
 */
export async function wrapDek(
  dek: string,
  wrappingSecret: string,
): Promise<EncryptedBlob> {
  return encryptToken(dek, wrappingSecret);
}

/** Unwrap a wrapped DEK back to its base64 string form. */
export async function unwrapDek(
  blob: EncryptedBlob,
  wrappingSecret: string,
): Promise<string> {
  return decryptToken(blob, wrappingSecret);
}
